import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import { toast } from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import '../../styles/admin-settings.css'

// ─── Shared sub-components ────────────────────────────────────────────────────

function Toggle({ checked, onChange, label }) {
  return (
    <label className="set-toggle">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="set-toggle-track" />
      {label && <span className="set-toggle-label">{label}</span>}
    </label>
  )
}

function SetSection({ title, description, children }) {
  return (
    <div className="set-section">
      <div className="set-section-header">
        <div className="set-section-title">{title}</div>
        {description && <div className="set-section-desc">{description}</div>}
      </div>
      <div className="set-section-body">{children}</div>
    </div>
  )
}

function SetField({ label, hint, children }) {
  return (
    <div className="set-field">
      {label && <label>{label}</label>}
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  )
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const IcoBrand = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
)

const IcoSecurity = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
)

const IcoEmail = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const IcoIntegrations = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
)

const IcoPlans = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
)

const IcoApi = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="9" x2="20" y2="9" />
    <line x1="4" y1="15" x2="20" y2="15" />
    <line x1="10" y1="3" x2="8" y2="21" />
    <line x1="16" y1="3" x2="14" y2="21" />
  </svg>
)

const IcoDanger = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
)

const IcoCustomPlans = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
  </svg>
)

const IcoCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const IcoX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const IcoEye = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const IcoEyeOff = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
)

const IcoUpload = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
)

const IcoPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const IcoTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
)

const IcoEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const IcoSave = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
)

const IcoHash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="9" x2="20" y2="9" />
    <line x1="4" y1="15" x2="20" y2="15" />
    <line x1="10" y1="3" x2="8" y2="21" />
    <line x1="16" y1="3" x2="14" y2="21" />
  </svg>
)

// ─── Nav config ───────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: 'brand',        label: 'Branding',      Icon: IcoBrand },
  { id: 'security',     label: 'Security',       Icon: IcoSecurity },
  { id: 'email',        label: 'Email',          Icon: IcoEmail },
  { id: 'integrations', label: 'Integrations',   Icon: IcoIntegrations },
  { id: 'plans',        label: 'Plans',          Icon: IcoPlans },
  { id: 'custom-plans', label: 'Custom Plans',   Icon: IcoCustomPlans },
  { id: 'api',          label: 'API & Tokens',   Icon: IcoApi },
  { id: 'danger',       label: 'Danger Zone',    Icon: IcoDanger, danger: true },
]

// ─── Brand tab ────────────────────────────────────────────────────────────────

function UploadSlot({ label, hint, dark, field, value, onUpload, onRemove, uploading }) {
  return (
    <div className="set-upload-slot">
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>{label}</div>
      <div className="set-upload-preview" style={{ background: dark ? 'var(--bg-3, #1a1625)' : 'var(--bg-0)' }}>
        {value
          ? <img src={value} alt={label} />
          : <IcoUpload />
        }
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <label className="ds-btn ds-btn-ghost" style={{ flex: 1, justifyContent: 'center', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
          {uploading ? 'Uploading…' : 'Replace'}
          <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploading}
            onChange={e => { if (e.target.files?.[0]) onUpload(e.target.files[0], field) }} />
        </label>
        {value && (
          <button className="ds-btn ds-btn-ghost" onClick={() => onRemove(field)}>Remove</button>
        )}
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

function BrandTab({ formData, setFormData, isSaving, handleSave }) {
  const [uploading, setUploading] = useState({})

  const handleUpload = async (file, field) => {
    setUploading(prev => ({ ...prev, [field]: true }))
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await apiService.post('/settings/branding/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setFormData(prev => ({ ...prev, branding: { ...prev.branding, [field]: res.data.url } }))
      toast.success('Image uploaded')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }))
    }
  }

  const handleRemove = (field) => {
    setFormData(prev => ({ ...prev, branding: { ...prev.branding, [field]: '' } }))
  }

  const BRAND_COLORS = ['#7C5CFF', '#A855F7', '#06B6D4', '#10B981', '#F59E0B', '#E8654F', '#0F1020']

  return (
    <>
      <SetSection title="Workspace identity" description="How this CMS appears to your clients">
        <div className="set-grid-2">
          <SetField label="CMS name">
            <input value={formData.branding?.cmsName || ''} onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, cmsName: e.target.value } }))} placeholder="WebNest" />
          </SetField>
          <SetField label="Support email">
            <input type="email" value={formData.branding?.supportEmail || ''} onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, supportEmail: e.target.value } }))} placeholder="support@youragency.com" />
          </SetField>
        </div>
        <SetField label="Login page tagline">
          <input value={formData.branding?.tagline || ''} onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, tagline: e.target.value } }))} placeholder="Powering your online presence" />
        </SetField>
        <SetField label="Copyright text">
          <input value={formData.branding?.copyrightText || ''} onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, copyrightText: e.target.value } }))} placeholder="© 2024 WebNest. All rights reserved." />
        </SetField>
        <SetField label="App URL">
          <input type="url" value={formData.branding?.appUrl || ''} onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, appUrl: e.target.value } }))} placeholder="https://cms.youragency.com" />
        </SetField>
      </SetSection>

      <SetSection title="Logo & favicon" description="Used in the admin sidebar, client portal and browser tabs">
        <div className="set-grid-2" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
          <UploadSlot label="Logo · dark mode" hint="White or light SVG / PNG" dark field="logoUrl" value={formData.branding?.logoUrl || ''} onUpload={handleUpload} onRemove={handleRemove} uploading={uploading.logoUrl} />
          <UploadSlot label="Logo · light mode" hint="Dark or black SVG / PNG" field="logoUrlLight" value={formData.branding?.logoUrlLight || ''} onUpload={handleUpload} onRemove={handleRemove} uploading={uploading.logoUrlLight} />
          <UploadSlot label="Favicon" hint="32×32 or 64×64" field="faviconUrl" value={formData.branding?.faviconUrl || ''} onUpload={handleUpload} onRemove={handleRemove} uploading={uploading.faviconUrl} />
        </div>
      </SetSection>

      <SetSection title="Brand color" description="Accent used in the portal and emails">
        <div className="set-color-swatches">
          {BRAND_COLORS.map((c, i) => (
            <button key={c} className={`set-swatch${i === 0 ? ' selected' : ''}`} style={{ background: c }} aria-label={c} />
          ))}
        </div>
      </SetSection>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="ds-btn" onClick={() => handleSave('branding')} disabled={isSaving}>
          <IcoSave /> {isSaving ? 'Saving…' : 'Save branding'}
        </button>
      </div>
    </>
  )
}

// ─── Security tab ─────────────────────────────────────────────────────────────

function SecurityTab({ formData, setFormData, isSaving, handleSave, adminSecurityInfo, setShow2FAModal }) {
  const AUDIT_LOG = [
    { t: 'Sara Voss enabled 2FA', time: 'May 24, 09:14' },
    { t: 'Theo Lin signed in from new device (Chrome · macOS)', time: 'May 23, 17:02' },
    { t: 'Failed login attempt for admin@cms.com', time: 'May 22, 11:48', warn: true },
    { t: 'API token rotated by Sara Voss', time: 'May 20, 15:20' },
    { t: 'Workspace SSO disabled by Sara Voss', time: 'May 18, 10:00' },
  ]

  return (
    <>
      <SetSection title="Your account 2FA" description="Protect your admin account with an authenticator app">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Two-factor authentication</div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 2 }}>
              {adminSecurityInfo?.totp_enabled
                ? 'Enabled — your account is protected with an authenticator app'
                : 'Add an extra layer of security to your account'}
            </div>
          </div>
          <button
            className="ds-btn"
            style={adminSecurityInfo?.totp_enabled ? { background: 'rgba(239,68,68,.1)', color: '#ef4444', borderColor: 'rgba(239,68,68,.3)' } : {}}
            onClick={() => setShow2FAModal(true)}
          >
            {adminSecurityInfo?.totp_enabled ? 'Disable 2FA' : 'Enable 2FA'}
          </button>
        </div>
      </SetSection>

      <SetSection title="Authentication" description="Sign-in security for the whole workspace">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Require 2FA for all admins</div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 2 }}>Force two-factor authentication workspace-wide</div>
          </div>
          <Toggle checked={formData.security?.requireTwoFactor || false} onChange={val => setFormData(prev => ({ ...prev, security: { ...prev.security, requireTwoFactor: val } }))} />
        </div>
        <div className="set-grid-2">
          <SetField label="Session timeout (hours)" hint="Auto sign-out after inactivity">
            <select value={formData.security?.sessionTimeout || 24} onChange={e => setFormData(prev => ({ ...prev, security: { ...prev.security, sessionTimeout: parseInt(e.target.value) } }))}>
              <option value={1}>1 hour</option>
              <option value={4}>4 hours</option>
              <option value={8}>8 hours</option>
              <option value={24}>24 hours</option>
              <option value={720}>30 days</option>
            </select>
          </SetField>
          <SetField label="Max login attempts">
            <input type="number" min={1} max={20} value={formData.security?.maxLoginAttempts || 5} onChange={e => setFormData(prev => ({ ...prev, security: { ...prev.security, maxLoginAttempts: parseInt(e.target.value) } }))} />
          </SetField>
        </div>
        <div className="set-grid-2">
          <SetField label="Min password length">
            <input type="number" min={6} max={64} value={formData.security?.passwordMinLength || 8} onChange={e => setFormData(prev => ({ ...prev, security: { ...prev.security, passwordMinLength: parseInt(e.target.value) } }))} />
          </SetField>
          <SetField label="Force HTTPS">
            <div style={{ paddingTop: 6 }}>
              <Toggle checked={formData.security?.forceHttps !== false} onChange={val => setFormData(prev => ({ ...prev, security: { ...prev.security, forceHttps: val } }))} label="Enabled" />
            </div>
          </SetField>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <button className="ds-btn" onClick={() => handleSave('security')} disabled={isSaving}>
            <IcoSave /> {isSaving ? 'Saving…' : 'Save security settings'}
          </button>
        </div>
      </SetSection>

      <SetSection title="Audit log" description="Recent security-related events">
        {AUDIT_LOG.map((l, i) => (
          <div key={i} className="set-audit-row">
            <span style={{ width: 20, height: 20, borderRadius: 6, background: l.warn ? 'rgba(239,68,68,.12)' : 'rgba(34,197,94,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: l.warn ? '#ef4444' : '#22c55e', flexShrink: 0 }}>
              {l.warn
                ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
                : <IcoCheck />
              }
            </span>
            <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text)' }}>{l.t}</span>
            <span style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono, monospace)' }}>{l.time}</span>
          </div>
        ))}
        <button className="ds-btn ds-btn-ghost" style={{ alignSelf: 'flex-start' }}>View full audit log</button>
      </SetSection>
    </>
  )
}

// ─── Email tab ────────────────────────────────────────────────────────────────

function EmailTab({ formData, setFormData, isSaving, handleSave, emailTestStatus, emailTestMessage, handleTestEmailConnection }) {
  const [showPw, setShowPw] = useState(false)

  return (
    <SetSection title="Email & notifications" description="SMTP server and notification preferences">
      <div className="set-grid-2">
        <SetField label="SMTP host">
          <input value={formData.email?.smtpHost || ''} onChange={e => setFormData(prev => ({ ...prev, email: { ...prev.email, smtpHost: e.target.value } }))} placeholder="smtp.gmail.com" />
        </SetField>
        <SetField label="SMTP port">
          <input type="number" value={formData.email?.smtpPort || 587} onChange={e => setFormData(prev => ({ ...prev, email: { ...prev.email, smtpPort: parseInt(e.target.value) } }))} placeholder="587" />
        </SetField>
      </div>
      <div className="set-grid-2">
        <SetField label="Username">
          <input value={formData.email?.smtpUsername || ''} onChange={e => setFormData(prev => ({ ...prev, email: { ...prev.email, smtpUsername: e.target.value } }))} placeholder="your@email.com" />
        </SetField>
        <SetField label="Password">
          <div className="set-pw-wrap">
            <input type={showPw ? 'text' : 'password'} value={formData.email?.smtpPassword || ''} onChange={e => setFormData(prev => ({ ...prev, email: { ...prev.email, smtpPassword: e.target.value } }))} placeholder="••••••••" />
            <button type="button" className="set-pw-toggle" onClick={() => setShowPw(s => !s)} aria-label="Toggle password visibility">
              {showPw ? <IcoEyeOff /> : <IcoEye />}
            </button>
          </div>
        </SetField>
      </div>
      <div className="set-grid-2">
        <SetField label="From email">
          <input type="email" value={formData.email?.fromEmail || ''} onChange={e => setFormData(prev => ({ ...prev, email: { ...prev.email, fromEmail: e.target.value } }))} placeholder="noreply@example.com" />
        </SetField>
        <SetField label="From name">
          <input value={formData.email?.fromName || ''} onChange={e => setFormData(prev => ({ ...prev, email: { ...prev.email, fromName: e.target.value } }))} placeholder="WebNest CMS" />
        </SetField>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Enable TLS</div>
          <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 2 }}>Use secure TLS connection for email delivery</div>
        </div>
        <Toggle checked={formData.email?.enableTls || false} onChange={val => setFormData(prev => ({ ...prev, email: { ...prev.email, enableTls: val } }))} />
      </div>
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button className="ds-btn ds-btn-ghost" onClick={handleTestEmailConnection} disabled={emailTestStatus === 'testing'}>
          {emailTestStatus === 'testing'
            ? <><span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite', marginRight: 6 }} />Testing…</>
            : 'Test connection'
          }
        </button>
        {emailTestStatus === 'success' && (
          <span style={{ fontSize: 13, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 6 }}>
            <IcoCheck /> {emailTestMessage}
          </span>
        )}
        {emailTestStatus === 'error' && (
          <span style={{ fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
            <IcoX /> {emailTestMessage}
          </span>
        )}
        <button className="ds-btn" style={{ marginLeft: 'auto' }} onClick={() => handleSave('email')} disabled={isSaving}>
          <IcoSave /> {isSaving ? 'Saving…' : 'Save email settings'}
        </button>
      </div>
    </SetSection>
  )
}

// ─── Integrations tab ─────────────────────────────────────────────────────────

const INTEGRATIONS = [
  { n: 'Slack',            desc: 'Get request alerts in #design-ops',      connected: true,  c: '#4A154B' },
  { n: 'Stripe',           desc: 'Bill clients automatically',              connected: true,  c: '#635BFF' },
  { n: 'GitHub',           desc: 'Sync site code with repos',               connected: true,  c: '#0F1020' },
  { n: 'Figma',            desc: 'Import designs as starting templates',    connected: false, c: '#A259FF' },
  { n: 'Linear',           desc: 'Open issues from edit requests',          connected: false, c: '#5E6AD2' },
  { n: 'Cloudflare',       desc: 'Manage DNS & SSL',                        connected: true,  c: '#F38020' },
  { n: 'Google Analytics', desc: 'Surface visitor data in dashboards',      connected: false, c: '#E37400' },
  { n: 'Notion',           desc: 'Mirror requests into a Notion DB',        connected: false, c: '#0F1020' },
]

function IntegrationsTab() {
  return (
    <SetSection title="Integrations" description="Connect WebNest to your other tools">
      <div className="integration-grid">
        {INTEGRATIONS.map(intg => (
          <div key={intg.n} className="integration-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: intg.c, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                {intg.n[0]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="integration-name">{intg.n}</span>
                  {intg.connected && (
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} title="Connected" />
                  )}
                </div>
                <div className="integration-desc">{intg.desc}</div>
              </div>
            </div>
            <button className={intg.connected ? 'ds-btn ds-btn-ghost' : 'ds-btn'} style={{ alignSelf: 'flex-start' }}>
              {intg.connected ? 'Manage' : 'Connect'}
            </button>
          </div>
        ))}
      </div>
    </SetSection>
  )
}

// ─── Plans tab ────────────────────────────────────────────────────────────────

function PlansTab({ formData, setFormData, isSaving, setIsSaving, updatePlansMutation }) {
  const [editingPlan, setEditingPlan] = useState(null)
  const [planForm, setPlanForm] = useState(null)

  const handleEditPlan = (planId) => {
    setEditingPlan(planId)
    setPlanForm({ ...formData.plans[planId] })
  }

  const handleSavePlan = () => {
    const updatedPlans = { ...formData.plans, [editingPlan]: planForm }
    setIsSaving(true)
    updatePlansMutation.mutate(updatedPlans)
    setEditingPlan(null)
    setPlanForm(null)
  }

  const handleCancel = () => {
    setEditingPlan(null)
    setPlanForm(null)
  }

  const PLAN_ORDER = ['basic', 'pro', 'enterprise']

  return (
    <>
      <SetSection title="Subscription plans" description="Manage client subscription plans, pricing, and features">
        <div className="plan-cards">
          {PLAN_ORDER.filter(k => formData.plans?.[k]).map(planId => {
            const plan = formData.plans[planId]
            return (
              <div key={planId} className={`plan-card${editingPlan === planId ? ' highlighted' : ''}`}>
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price">£{plan.price}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span></div>
                <div className="plan-feat-list">
                  <div className="plan-feat-item">
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Sites:</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12 }}>{plan.features?.sites === 'unlimited' ? 'Unlimited' : plan.features?.sites}</span>
                  </div>
                  <div className="plan-feat-item">
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Edits/mo:</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12 }}>{plan.features?.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features?.editsPerMonth}</span>
                  </div>
                  <div className="plan-feat-item">
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Storage:</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12 }}>{plan.features?.storage}</span>
                  </div>
                  {[
                    { key: 'customDomain', label: 'Custom domain' },
                    { key: 'analytics', label: 'Analytics' },
                    { key: 'backup', label: 'Backup' },
                  ].map(({ key, label }) => (
                    <div key={key} className="plan-feat-item" style={{ color: plan.features?.[key] ? 'var(--text)' : 'var(--text-faint)' }}>
                      <span style={{ color: plan.features?.[key] ? 'var(--accent)' : 'var(--border)', marginRight: 6 }}><IcoCheck /></span>
                      {label}
                    </div>
                  ))}
                </div>
                <button className="ds-btn ds-btn-ghost" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={() => handleEditPlan(planId)}>
                  <IcoEdit /> Edit plan
                </button>
              </div>
            )
          })}
        </div>
      </SetSection>

      {editingPlan && planForm && (
        <div className="set-plan-edit">
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Edit plan: {planForm.name}</div>
          <div className="set-grid-2" style={{ marginBottom: 16 }}>
            <SetField label="Plan name">
              <input value={planForm.name} onChange={e => setPlanForm(p => ({ ...p, name: e.target.value }))} />
            </SetField>
            <SetField label="Price (£/month)">
              <input type="number" value={planForm.price} onChange={e => setPlanForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} />
            </SetField>
          </div>
          <div className="set-grid-2" style={{ marginBottom: 16 }}>
            <SetField label="Number of sites" hint="Enter 'unlimited' for unlimited">
              <input value={planForm.features?.sites === 'unlimited' ? 'unlimited' : planForm.features?.sites} onChange={e => { const v = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 1; setPlanForm(p => ({ ...p, features: { ...p.features, sites: v } })) }} />
            </SetField>
            <SetField label="Edits per month" hint="Enter 'unlimited' for unlimited">
              <input value={planForm.features?.editsPerMonth === 'unlimited' ? 'unlimited' : planForm.features?.editsPerMonth} onChange={e => { const v = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 50; setPlanForm(p => ({ ...p, features: { ...p.features, editsPerMonth: v } })) }} />
            </SetField>
          </div>
          <div className="set-grid-2" style={{ marginBottom: 16 }}>
            <SetField label="Storage">
              <input value={planForm.features?.storage || ''} onChange={e => setPlanForm(p => ({ ...p, features: { ...p.features, storage: e.target.value } }))} placeholder="1GB Storage" />
            </SetField>
            <SetField label="Support type">
              <input value={planForm.features?.support || ''} onChange={e => setPlanForm(p => ({ ...p, features: { ...p.features, support: e.target.value } }))} placeholder="Email Support" />
            </SetField>
          </div>
          <div className="set-grid-2" style={{ marginBottom: 16 }}>
            <SetField label="Stripe Price ID (optional)">
              <input value={planForm.stripePriceId || ''} onChange={e => setPlanForm(p => ({ ...p, stripePriceId: e.target.value }))} placeholder="price_..." />
            </SetField>
            <SetField label="Stripe Product ID (optional)">
              <input value={planForm.stripeProductId || ''} onChange={e => setPlanForm(p => ({ ...p, stripeProductId: e.target.value }))} placeholder="prod_..." />
            </SetField>
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            {['customDomain', 'analytics', 'backup'].map(key => (
              <Toggle key={key} checked={planForm.features?.[key] || false} onChange={val => setPlanForm(p => ({ ...p, features: { ...p.features, [key]: val } }))} label={{ customDomain: 'Custom domain', analytics: 'Analytics', backup: 'Backup' }[key]} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="ds-btn ds-btn-ghost" onClick={handleCancel}>Cancel</button>
            <button className="ds-btn" onClick={handleSavePlan} disabled={isSaving}><IcoSave /> Save plan</button>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Custom Plans tab ─────────────────────────────────────────────────────────

function CustomPlansTab({ customPlans, isSaving, setIsSaving, createCustomPlanMutation, updateCustomPlanMutation, deleteCustomPlanMutation }) {
  const blankForm = () => ({
    name: '', price: 0, description: '', targetType: 'custom', targetEmails: [],
    stripePriceId: '', stripeProductId: '',
    features: { sites: 1, editsPerMonth: 50, storage: '1GB Storage', support: 'Email Support', customDomain: false, analytics: false, backup: false, priority: 'normal' }
  })

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(blankForm())
  const [emailInput, setEmailInput] = useState('')

  const addEmail = () => {
    const e = emailInput.trim()
    if (e && !form.targetEmails.includes(e)) {
      setForm(p => ({ ...p, targetEmails: [...p.targetEmails, e] }))
      setEmailInput('')
    }
  }

  const removeEmail = (e) => setForm(p => ({ ...p, targetEmails: p.targetEmails.filter(x => x !== e) }))

  const handleCreate = () => {
    setIsSaving(true)
    createCustomPlanMutation.mutate(form)
    setShowForm(false)
    setForm(blankForm())
  }

  const handleUpdate = () => {
    setIsSaving(true)
    updateCustomPlanMutation.mutate({ planId: editingId, planData: form })
    setEditingId(null)
    setShowForm(false)
    setForm(blankForm())
  }

  const handleEdit = (plan) => {
    setEditingId(plan.id)
    setForm({ name: plan.name, price: plan.price, description: plan.description || '', targetType: plan.targetType, targetEmails: plan.targetEmails || [], stripePriceId: plan.stripePriceId || '', stripeProductId: plan.stripeProductId || '', features: { ...plan.features } })
    setShowForm(true)
  }

  const handleDelete = (planId) => {
    if (window.confirm('Delete this custom plan? This cannot be undone.')) {
      deleteCustomPlanMutation.mutate(planId)
    }
  }

  const handleCancel = () => { setShowForm(false); setEditingId(null); setForm(blankForm()) }

  return (
    <>
      <SetSection title="Custom plans" description="Special pricing for friends, family, or specific customers">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="ds-btn" onClick={() => setShowForm(true)}><IcoPlus /> Create custom plan</button>
        </div>

        {customPlans && Object.keys(customPlans).length > 0 ? (
          <div className="set-custom-plans-grid">
            {Object.values(customPlans).map(plan => (
              <div key={plan.id} className="set-custom-plan-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', flex: 1 }}>{plan.name}</span>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'var(--bg-0)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>{plan.targetType}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>£{plan.price}<span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span></div>
                {plan.description && <div style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 8 }}>{plan.description}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                  {plan.features?.sites === 'unlimited' ? 'Unlimited' : plan.features?.sites} sites · {plan.features?.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features?.editsPerMonth} edits/mo · {plan.features?.storage}
                </div>
                {plan.targetEmails?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                    {plan.targetEmails.map(e => <span key={e} className="set-chip">{e}</span>)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="ds-btn ds-btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handleEdit(plan)}><IcoEdit /> Edit</button>
                  <button className="ds-btn ds-btn-ghost" style={{ flex: 1, justifyContent: 'center', color: '#ef4444' }} onClick={() => handleDelete(plan.id)}><IcoTrash /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-faint)', fontSize: 13 }}>
            No custom plans yet. Create one to offer special pricing.
          </div>
        )}
      </SetSection>

      {showForm && (
        <div className="set-plan-edit">
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
            {editingId ? 'Edit custom plan' : 'Create custom plan'}
          </div>
          <div className="set-grid-2" style={{ marginBottom: 16 }}>
            <SetField label="Plan name"><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Friends & Family Plan" /></SetField>
            <SetField label="Price (£/month)"><input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} /></SetField>
          </div>
          <SetField label="Description" hint="Optional"><textarea rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Special pricing for friends and family" /></SetField>
          <div style={{ margin: '16px 0 8px', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Plan type</div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            {[{ value: 'friend', label: 'Friends' }, { value: 'family', label: 'Family' }, { value: 'custom', label: 'Custom' }].map(({ value, label }) => (
              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="radio" name="customPlanType" checked={form.targetType === value} onChange={() => setForm(p => ({ ...p, targetType: value }))} />
                {label}
              </label>
            ))}
          </div>
          <SetField label="Target emails (optional)" hint="Press Enter or click Add to add an email">
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ flex: 1 }} type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmail())} placeholder="user@example.com" />
              <button type="button" className="ds-btn ds-btn-ghost" onClick={addEmail}>Add</button>
            </div>
            {form.targetEmails.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {form.targetEmails.map(e => (
                  <span key={e} className="set-chip">{e}<button onClick={() => removeEmail(e)} aria-label="Remove"><IcoX /></button></span>
                ))}
              </div>
            )}
          </SetField>
          <div style={{ margin: '16px 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Plan features</div>
          <div className="set-grid-2" style={{ marginBottom: 16 }}>
            <SetField label="Sites"><input value={form.features.sites === 'unlimited' ? 'unlimited' : form.features.sites} onChange={e => { const v = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 1; setForm(p => ({ ...p, features: { ...p.features, sites: v } })) }} /></SetField>
            <SetField label="Edits/month"><input value={form.features.editsPerMonth === 'unlimited' ? 'unlimited' : form.features.editsPerMonth} onChange={e => { const v = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 50; setForm(p => ({ ...p, features: { ...p.features, editsPerMonth: v } })) }} /></SetField>
          </div>
          <div className="set-grid-2" style={{ marginBottom: 16 }}>
            <SetField label="Storage"><input value={form.features.storage} onChange={e => setForm(p => ({ ...p, features: { ...p.features, storage: e.target.value } }))} placeholder="1GB Storage" /></SetField>
            <SetField label="Support type"><input value={form.features.support} onChange={e => setForm(p => ({ ...p, features: { ...p.features, support: e.target.value } }))} placeholder="Email Support" /></SetField>
          </div>
          <div className="set-grid-2" style={{ marginBottom: 16 }}>
            <SetField label="Stripe Price ID (optional)"><input value={form.stripePriceId} onChange={e => setForm(p => ({ ...p, stripePriceId: e.target.value }))} placeholder="price_..." /></SetField>
            <SetField label="Stripe Product ID (optional)"><input value={form.stripeProductId} onChange={e => setForm(p => ({ ...p, stripeProductId: e.target.value }))} placeholder="prod_..." /></SetField>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            {['customDomain', 'analytics', 'backup'].map(key => (
              <Toggle key={key} checked={form.features[key] || false} onChange={val => setForm(p => ({ ...p, features: { ...p.features, [key]: val } }))} label={{ customDomain: 'Custom domain', analytics: 'Analytics', backup: 'Backup' }[key]} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="ds-btn ds-btn-ghost" onClick={handleCancel}>Cancel</button>
            <button className="ds-btn" onClick={editingId ? handleUpdate : handleCreate} disabled={!form.name || form.price < 0 || isSaving}>
              {editingId ? 'Update plan' : 'Create plan'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

// ─── API tab ──────────────────────────────────────────────────────────────────

function ApiTab() {
  const TOKENS = [
    { n: 'Production deployer', l: 'wn_prod_********f3a2', c: 'Jan 12, 2026', u: '2m ago' },
    { n: 'Analytics export',    l: 'wn_anlt_********7b9c', c: 'Mar 04, 2026', u: '1d ago' },
  ]

  return (
    <>
      <SetSection title="API tokens" description="Use to script WebNest from your tools">
        {TOKENS.map(t => (
          <div key={t.n} className="set-token-row">
            <span style={{ color: 'var(--text-muted)' }}><IcoHash /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{t.n}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono, monospace)', marginTop: 2 }}>
                <span className="api-key-preview">{t.l}</span> · created {t.c} · last used {t.u}
              </div>
            </div>
            <button className="ds-btn ds-btn-ghost">Rotate</button>
          </div>
        ))}
        <button className="ds-btn ds-btn-ghost" style={{ alignSelf: 'flex-start' }}><IcoPlus /> New token</button>
      </SetSection>

      <SetSection title="Webhooks" description="Push events to your endpoints">
        <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, fontFamily: 'var(--font-mono, monospace)', fontSize: 12, color: 'var(--text-muted)' }}>
          POST https://hooks.northstar.studio/webnest
          <span style={{ display: 'inline-block', marginLeft: 8, padding: '1px 7px', background: 'rgba(34,197,94,.12)', color: '#22c55e', borderRadius: 20, fontSize: 11, fontFamily: 'inherit' }}>active</span>
          <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>Events: site.published, request.opened, payment.failed</div>
        </div>
      </SetSection>
    </>
  )
}

// ─── Danger tab ───────────────────────────────────────────────────────────────

function DangerTab() {
  const ACTIONS = [
    { l: 'Transfer ownership', desc: 'Move this workspace to another admin', danger: false },
    { l: 'Archive workspace',  desc: 'Read-only, keeps data for 90 days',    danger: false },
    { l: 'Delete workspace',   desc: 'Permanently delete — cannot be undone', danger: true },
  ]

  return (
    <div className="danger-zone">
      <div className="danger-zone-title">Danger zone</div>
      <div className="danger-zone-desc">These actions are irreversible. Please be certain before proceeding.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ACTIONS.map(d => (
          <div key={d.l} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: 13, color: d.danger ? '#ef4444' : 'var(--text)' }}>{d.l}</div>
              <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 2 }}>{d.desc}</div>
            </div>
            <button
              className="ds-btn ds-btn-ghost"
              style={d.danger ? { color: '#ef4444', borderColor: 'rgba(239,68,68,.3)', background: 'rgba(239,68,68,.07)' } : {}}
              onClick={() => { if (window.confirm(`Are you sure you want to "${d.l}"? This may be irreversible.`)) { /* placeholder */ } }}
            >
              {d.l.split(' ')[0]}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── 2FA Modal ────────────────────────────────────────────────────────────────

function AdminTwoFactorModal({ isEnabled, onClose }) {
  const [step, setStep] = useState(isEnabled ? 'disable' : 'info')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSetup = async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await apiService.post('/auth/2fa/setup')
      setQrCode(response.data.qrCode)
      setSecret(response.data.secret)
      setStep('setup')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to setup 2FA')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      await apiService.post('/auth/2fa/verify', { token: code })
      setStep('success')
      toast.success('2FA enabled successfully!')
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisable = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      await apiService.post('/auth/2fa/disable', { token: code })
      setStep('disabled')
      toast.success('2FA disabled')
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid authenticator code')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="set-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="set-modal">
        <div className="set-modal-header">
          <span className="set-modal-title">Two-Factor Authentication</span>
          <button className="ds-btn ds-btn-ghost" style={{ padding: '4px 8px' }} onClick={onClose} aria-label="Close">
            <IcoX />
          </button>
        </div>
        <div className="set-modal-body">
          {step === 'info' && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Adds an extra layer of security — you'll need a code from your authenticator app each time you log in.
              </p>
              <ul style={{ fontSize: 13, color: 'var(--text-muted)', paddingLeft: 18, lineHeight: 1.8 }}>
                <li>Google Authenticator</li>
                <li>Authy</li>
                <li>Any TOTP-compatible app</li>
              </ul>
              {error && <div className="set-error-box"><IcoX /> {error}</div>}
            </>
          )}
          {step === 'setup' && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.</p>
              {qrCode && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 16, background: 'white', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <img src={qrCode} alt="2FA QR Code" style={{ width: 160, height: 160 }} />
                </div>
              )}
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 6 }}>Can't scan? Enter this key manually:</div>
                <div className="set-secret-code">{secret}</div>
              </div>
              <form onSubmit={handleVerify}>
                <input type="text" inputMode="numeric" className="set-otp-input" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoFocus required />
              </form>
              {error && <div className="set-error-box"><IcoX /> {error}</div>}
            </>
          )}
          {step === 'disable' && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Enter the current 6-digit code from your authenticator app to disable 2FA.</p>
              <form onSubmit={handleDisable}>
                <input type="text" inputMode="numeric" className="set-otp-input" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoFocus required />
              </form>
              {error && <div className="set-error-box"><IcoX /> {error}</div>}
            </>
          )}
          {(step === 'success' || step === 'disabled') && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(34,197,94,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', color: '#22c55e' }}>
                <IcoCheck />
              </div>
              <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>
                {step === 'success' ? '2FA enabled successfully!' : '2FA has been disabled'}
              </div>
            </div>
          )}
        </div>
        {!['success', 'disabled'].includes(step) && (
          <div className="set-modal-footer">
            {step === 'info' && (
              <>
                <button className="ds-btn ds-btn-ghost" onClick={onClose}>Cancel</button>
                <button className="ds-btn" onClick={handleSetup} disabled={isLoading}>{isLoading ? 'Loading…' : 'Get started'}</button>
              </>
            )}
            {step === 'setup' && (
              <>
                <button className="ds-btn ds-btn-ghost" onClick={() => setStep('info')}>Back</button>
                <button className="ds-btn" onClick={handleVerify} disabled={isLoading || code.length !== 6}>{isLoading ? 'Verifying…' : 'Verify & enable'}</button>
              </>
            )}
            {step === 'disable' && (
              <>
                <button className="ds-btn ds-btn-ghost" onClick={onClose}>Cancel</button>
                <button className="ds-btn" style={{ background: 'rgba(239,68,68,.1)', color: '#ef4444', borderColor: 'rgba(239,68,68,.3)' }} onClick={handleDisable} disabled={isLoading || code.length !== 6}>{isLoading ? 'Disabling…' : 'Disable 2FA'}</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('brand')
  const [isSaving, setIsSaving] = useState(false)
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [emailTestStatus, setEmailTestStatus] = useState(null) // null | 'testing' | 'success' | 'error'
  const [emailTestMessage, setEmailTestMessage] = useState('')
  const queryClient = useQueryClient()

  // ── Queries (preserved exactly) ──────────────────────────────────────────
  const { data: adminSecurityInfo } = useQuery(
    'admin-user-security',
    () => apiService.get('/auth/security').then(r => r.data),
    { staleTime: 0 }
  )

  const { data: settings, isLoading } = useQuery('settings', () => apiService.get('/settings').then(res => res.data))

  const { data: billingPlans } = useQuery('billing-plans', () => apiService.get('/billing/plans').then(res => res.data))

  const { data: customPlans } = useQuery('custom-plans', () => apiService.get('/custom-plans').then(res => res.data))

  // ── Mutations (preserved exactly) ────────────────────────────────────────
  const updateSettingsMutation = useMutation(
    (newSettings) => apiService.put('/settings', newSettings).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('settings')
        queryClient.invalidateQueries('billing-plans')
        toast.success('Settings updated successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings')
        setIsSaving(false)
      }
    }
  )

  const updatePlansMutation = useMutation(
    (plans) => apiService.put('/billing/plans', plans).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('billing-plans')
        queryClient.invalidateQueries('settings')
        queryClient.refetchQueries('billing-plans')
        toast.success('Plans updated successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        console.error('Update plans error:', error)
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update plans')
        setIsSaving(false)
      }
    }
  )

  const createCustomPlanMutation = useMutation(
    (planData) => apiService.post('/custom-plans', planData).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-plans')
        toast.success('Custom plan created successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        console.error('Create custom plan error:', error)
        toast.error(error.response?.data?.message || 'Failed to create custom plan')
        setIsSaving(false)
      }
    }
  )

  const updateCustomPlanMutation = useMutation(
    ({ planId, planData }) => apiService.put(`/custom-plans/${planId}`, planData).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-plans')
        toast.success('Custom plan updated successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        console.error('Update custom plan error:', error)
        toast.error(error.response?.data?.message || 'Failed to update custom plan')
        setIsSaving(false)
      }
    }
  )

  const deleteCustomPlanMutation = useMutation(
    (planId) => apiService.delete(`/custom-plans/${planId}`).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-plans')
        toast.success('Custom plan deleted successfully')
      },
      onError: (error) => {
        console.error('Delete custom plan error:', error)
        toast.error(error.response?.data?.message || 'Failed to delete custom plan')
      }
    }
  )

  // ── Default settings structure ────────────────────────────────────────────
  const defaultSettings = {
    general: {
      siteName: 'WebNest CMS',
      siteDescription: 'Professional CMS for managing websites',
      adminEmail: 'admin@webnest.com',
      timezone: 'UTC',
      language: 'en',
      maintenanceMode: false,
      registrationEnabled: true
    },
    security: {
      requireTwoFactor: false,
      sessionTimeout: 24,
      maxLoginAttempts: 5,
      passwordMinLength: 8,
      requirePasswordChange: false,
      forceHttps: true,
      ipWhitelist: [],
      auditLogging: true
    },
    email: {
      provider: 'smtp',
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      smtpUsername: '',
      smtpPassword: '',
      fromEmail: 'noreply@webnest.com',
      fromName: 'WebNest',
      enableTls: true,
      testConnection: 'pending'
    },
    storage: {
      maxFileSize: 10,
      allowedTypes: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx'],
      storageQuota: 1000,
      backupEnabled: true,
      backupFrequency: 'daily',
      compressionEnabled: true,
      cdnEnabled: false
    },
    appearance: {
      primaryColor: '#4f46e5',
      secondaryColor: '#059669',
      darkMode: true,
      companyLogo: '',
      favicon: '',
      customCss: ''
    },
    branding: {
      cmsName: 'WebNest',
      tagline: 'Powering your online presence',
      supportEmail: '',
      copyrightText: '© 2024 WebNest. All rights reserved.',
      logoUrl: '',
      logoUrlLight: '',
      faviconUrl: '',
      appUrl: ''
    },
    notifications: {
      emailNotifications: true,
      systemAlerts: true,
      maintenanceAlerts: true,
      securityAlerts: true,
      reportFrequency: 'weekly'
    },
    plans: {
      basic: {
        name: 'Basic',
        price: 29,
        features: { sites: 1, editsPerMonth: 50, storage: '1GB Storage', support: 'Email Support', customDomain: true, analytics: false, backup: false, priority: 'normal' }
      },
      pro: {
        name: 'Pro',
        price: 79,
        features: { sites: 5, editsPerMonth: 200, storage: '10GB Storage', support: 'Priority Support', customDomain: true, analytics: true, backup: true, priority: 'high' }
      },
      enterprise: {
        name: 'Enterprise',
        price: 199,
        features: { sites: 'unlimited', editsPerMonth: 'unlimited', storage: '100GB Storage', support: '24/7 Phone Support', customDomain: true, analytics: true, backup: true, priority: 'highest' }
      }
    }
  }

  const displaySettings = useMemo(() => {
    const baseSettings = settings || defaultSettings
    return {
      ...baseSettings,
      plans: billingPlans ? {
        basic:      billingPlans.basic      || baseSettings.plans.basic,
        pro:        billingPlans.pro        || baseSettings.plans.pro,
        enterprise: billingPlans.enterprise || baseSettings.plans.enterprise
      } : baseSettings.plans
    }
  }, [settings, billingPlans])

  const [formData, setFormData] = useState(displaySettings)

  useEffect(() => {
    if (!isSaving) {
      setFormData(displaySettings)
    }
  }, [displaySettings, isSaving])

  const handleSave = async (section) => {
    setIsSaving(true)
    if (section === 'plans') {
      updatePlansMutation.mutate(formData.plans)
    } else {
      updateSettingsMutation.mutate({
        ...displaySettings,
        [section]: formData[section]
      })
    }
  }

  const handleTestEmailConnection = async () => {
    setEmailTestStatus('testing')
    setEmailTestMessage('')
    try {
      const res = await apiService.get('/emails/verify')
      if (res.data?.verified) {
        setEmailTestStatus('success')
        setEmailTestMessage('Connection successful — your SMTP settings are working.')
      } else {
        setEmailTestStatus('error')
        setEmailTestMessage(res.data?.message || 'Connection failed. Check your SMTP settings.')
      }
    } catch (err) {
      setEmailTestStatus('error')
      setEmailTestMessage(err.response?.data?.details || err.response?.data?.error || 'Could not reach the email server. Check your settings.')
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <LoadingSpinner size="lg" text="Loading settings…" />
      </div>
    )
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Settings</div>
          <h1>Settings</h1>
          <div className="subtitle">Branding, security, integrations and client plans</div>
        </div>
      </div>

      <div className="page-body">
        <div className="settings-layout">
          {/* Sidebar nav */}
          <nav className="settings-nav" aria-label="Settings sections">
            {NAV_ITEMS.map(({ id, label, Icon, danger }) => (
              <button
                key={id}
                className={`settings-nav-item${activeTab === id ? ' active' : ''}`}
                onClick={() => setActiveTab(id)}
                style={danger && activeTab !== id ? { color: '#ef4444' } : undefined}
              >
                <Icon />
                {label}
              </button>
            ))}
          </nav>

          {/* Content pane */}
          <div>
            {activeTab === 'brand' && (
              <BrandTab formData={formData} setFormData={setFormData} isSaving={isSaving} handleSave={handleSave} />
            )}
            {activeTab === 'security' && (
              <SecurityTab
                formData={formData}
                setFormData={setFormData}
                isSaving={isSaving}
                handleSave={handleSave}
                adminSecurityInfo={adminSecurityInfo}
                setShow2FAModal={setShow2FAModal}
              />
            )}
            {activeTab === 'email' && (
              <EmailTab
                formData={formData}
                setFormData={setFormData}
                isSaving={isSaving}
                handleSave={handleSave}
                emailTestStatus={emailTestStatus}
                emailTestMessage={emailTestMessage}
                handleTestEmailConnection={handleTestEmailConnection}
              />
            )}
            {activeTab === 'integrations' && <IntegrationsTab />}
            {activeTab === 'plans' && (
              <PlansTab
                formData={formData}
                setFormData={setFormData}
                isSaving={isSaving}
                setIsSaving={setIsSaving}
                updatePlansMutation={updatePlansMutation}
              />
            )}
            {activeTab === 'custom-plans' && (
              <CustomPlansTab
                customPlans={customPlans}
                isSaving={isSaving}
                setIsSaving={setIsSaving}
                createCustomPlanMutation={createCustomPlanMutation}
                updateCustomPlanMutation={updateCustomPlanMutation}
                deleteCustomPlanMutation={deleteCustomPlanMutation}
              />
            )}
            {activeTab === 'api' && <ApiTab />}
            {activeTab === 'danger' && <DangerTab />}
          </div>
        </div>
      </div>

      {show2FAModal && (
        <AdminTwoFactorModal
          isEnabled={!!adminSecurityInfo?.totp_enabled}
          onClose={() => { setShow2FAModal(false); queryClient.invalidateQueries('admin-user-security') }}
        />
      )}
    </>
  )
}

export default AdminSettings
